import type { ApiResult } from '../game/http-types.js';

type GoogleAccountsId = {
  initialize(options: { client_id: string; callback: (response: { credential?: string }) => void; auto_select?: boolean }): void;
  renderButton(container: HTMLElement, options: { type: 'standard'; theme: 'outline'; size: 'large'; text: 'signin_with'; width: number }): void;
  disableAutoSelect?(): void;
};
type GoogleWindow = Window & { google?: { accounts?: { id?: GoogleAccountsId } } };
let scriptPromise: Promise<void> | undefined;

function googleId(): GoogleAccountsId | undefined {
  return typeof window === 'undefined' ? undefined : (window as GoogleWindow).google?.accounts?.id;
}

function loadGisScript(): Promise<void> {
  if (googleId()) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google 로그인 스크립트를 불러오지 못했습니다.'));
      document.head.appendChild(script);
    }).catch((error: unknown) => { scriptPromise = undefined; throw error; });
  }
  return scriptPromise;
}

/** Render Google's official button only when the RPG server has enabled its verified identity boundary. */
export async function mountGoogleSignIn(
  container: HTMLElement,
  onCredential: (credential: string) => Promise<void>,
  onError: (message: string) => void,
): Promise<void> {
  container.hidden = true;
  try {
    const response = await fetch('/api/auth/google/config', { credentials: 'same-origin' });
    const result = await response.json() as ApiResult<{ enabled: boolean; clientId?: string }>;
    if (!response.ok || !result.ok) throw new Error('Google 로그인 설정을 불러오지 못했습니다.');
    if (!result.value.enabled) return;
    const clientId = result.value.clientId;
    if (!clientId || !/^[0-9A-Za-z_-]+\.apps\.googleusercontent\.com$/.test(clientId)) throw new Error('Google 로그인 설정이 올바르지 않습니다.');
    await loadGisScript();
    const id = googleId();
    if (!id) throw new Error('Google 로그인 스크립트를 초기화하지 못했습니다.');
    let loginInFlight = false;
    id.initialize({ client_id: clientId, auto_select: false, callback: (response) => {
      if (loginInFlight) return;
      const credential = response.credential;
      if (typeof credential !== 'string' || !credential) { onError('Google 인증 정보를 받지 못했습니다.'); return; }
      loginInFlight = true;
      let pending: Promise<void>;
      try { pending = onCredential(credential); }
      catch { loginInFlight = false; onError('Google 로그인에 실패했습니다.'); return; }
      void Promise.resolve(pending).catch(() => onError('Google 로그인에 실패했습니다.')).finally(() => { loginInFlight = false; });
    } });
    id.renderButton(container, { type: 'standard', theme: 'outline', size: 'large', text: 'signin_with', width: 280 });
    container.hidden = false;
  } catch (error) {
    container.hidden = true;
    onError(error instanceof Error ? error.message : 'Google 로그인을 초기화하지 못했습니다.');
  }
}

/** Local app logout only; this does not sign out of the user's Google account. */
export function disableGoogleAutoSelect(): void { googleId()?.disableAutoSelect?.(); }
